import { Column } from 'primereact/column';
import { DataTable, type DataTableRowClickEvent } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import React, { useEffect, useMemo, useState } from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    H6Emphasized,
    Spinner,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { ConfirmModal } from '../../common/components/ConfirmModal';
import { Button } from '../../common/components/GroveButtons';
import { MutationSuccessToastOptions } from '../../common/components/Toasts';
import {
    type InterestRateChangeLogListItem,
    type InterestRateChangeLogsQuery,
    type PendingFutureRateWindow,
    type ProductLogHistoryMismatch,
    useGetInterestRateAudit,
    useGetInterestRateChangeLogs,
    useGetInterestRateProductLogHistoryRecon,
    useGetInterestRateRecon,
    useGetInterestRateSchedule,
    useRetryMembershipInterestRateSync,
    useTriggerMassTierChange,
} from '../hooks/useInterestRateOps';

const STATUS_OPTIONS = [
    { label: 'Any status', value: '' },
    { label: 'FAILED', value: 'FAILED' },
    { label: 'SUCCEEDED', value: 'SUCCEEDED' },
    { label: 'SKIPPED', value: 'SKIPPED' },
    { label: 'PENDING', value: 'PENDING' },
];

const pendingWindowLabel = (window: PendingFutureRateWindow): string =>
    `${window.tier} @ ${window.validFrom} → ${window.apyDisplay} (${window.rateBps} bps)`;

const formatRatesJson = (rates: unknown): string =>
    JSON.stringify(rates, null, 2) ?? 'null';

const MONO_LINE_STYLE: React.CSSProperties = {
    display: 'block',
    whiteSpace: 'pre',
    paddingLeft: 8,
    paddingRight: 8,
    lineHeight: '18px',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: 12,
};

const RatesJsonSideBySideDiff = ({
    expectedRates,
    actualRates,
}: {
    expectedRates: unknown;
    actualRates: unknown;
}) => {
    const leftRef = React.useRef<HTMLDivElement | null>(null);
    const rightRef = React.useRef<HTMLDivElement | null>(null);
    const syncing = React.useRef(false);

    const expectedLines = formatRatesJson(expectedRates).split('\n');
    const actualLines = formatRatesJson(actualRates).split('\n');
    const rowCount = Math.max(expectedLines.length, actualLines.length);
    const rows = Array.from({ length: rowCount }, (_, index) => {
        const expected = expectedLines[index] ?? '';
        const actual = actualLines[index] ?? '';
        return {
            expected,
            actual,
            differs: expected !== actual,
        };
    });

    const syncScroll = (source: 'left' | 'right') => {
        if (syncing.current) {
            return;
        }
        const from = source === 'left' ? leftRef.current : rightRef.current;
        const to = source === 'left' ? rightRef.current : leftRef.current;
        if (from == null || to == null) {
            return;
        }
        syncing.current = true;
        to.scrollTop = from.scrollTop;
        to.scrollLeft = from.scrollLeft;
        requestAnimationFrame(() => {
            syncing.current = false;
        });
    };

    return (
        <div
            style={{
                display: 'flex',
                width: '100%',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                overflow: 'hidden',
                backgroundColor: '#fff',
            }}
        >
            <div
                style={{
                    flex: 1,
                    minWidth: 0,
                    borderRight: '1px solid #d1d5db',
                }}
            >
                <div
                    style={{
                        padding: '6px 8px',
                        backgroundColor: '#f3f4f6',
                        fontSize: 13,
                        fontWeight: 600,
                    }}
                >
                    Left · expected (product log)
                </div>
                <div
                    ref={leftRef}
                    onScroll={() => {
                        syncScroll('left');
                    }}
                    style={{ maxHeight: 480, overflow: 'auto' }}
                >
                    {rows.map((row, index) => (
                        <span
                            key={`expected-${index}`}
                            style={{
                                ...MONO_LINE_STYLE,
                                backgroundColor: row.differs
                                    ? '#dcfce7'
                                    : 'transparent',
                            }}
                        >
                            {row.expected.length > 0 ? row.expected : ' '}
                        </span>
                    ))}
                </div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div
                    style={{
                        padding: '6px 8px',
                        backgroundColor: '#f3f4f6',
                        fontSize: 13,
                        fontWeight: 600,
                    }}
                >
                    Right · actual (change log, clipped)
                </div>
                <div
                    ref={rightRef}
                    onScroll={() => {
                        syncScroll('right');
                    }}
                    style={{ maxHeight: 480, overflow: 'auto' }}
                >
                    {rows.map((row, index) => (
                        <span
                            key={`actual-${index}`}
                            style={{
                                ...MONO_LINE_STYLE,
                                backgroundColor: row.differs
                                    ? '#fee2e2'
                                    : 'transparent',
                            }}
                        >
                            {row.actual.length > 0 ? row.actual : ' '}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
};

export const BankOpsApyRatesPanel = () => {
    const toast = useToastController();

    const {
        data: schedule,
        isLoading: isLoadingSchedule,
        refetch: refetchSchedule,
    } = useGetInterestRateSchedule({});
    const {
        data: audit,
        isLoading: isLoadingAudit,
        refetch: refetchAudit,
    } = useGetInterestRateAudit({});

    const [logStatus, setLogStatus] = useState('FAILED');
    const [logMembershipId, setLogMembershipId] = useState('');
    const [logBankAccountId, setLogBankAccountId] = useState('');
    const [logOffset, setLogOffset] = useState(0);
    const logLimit = 50;

    const changeLogQuery = useMemo((): InterestRateChangeLogsQuery => {
        const query: InterestRateChangeLogsQuery = {
            limit: logLimit,
            offset: logOffset,
        };
        if (logStatus !== '') {
            query.status = logStatus;
        }
        if (logMembershipId.trim() !== '') {
            query.membershipId = logMembershipId.trim();
        }
        if (logBankAccountId.trim() !== '') {
            query.bankAccountId = logBankAccountId.trim();
        }
        return query;
    }, [logStatus, logMembershipId, logBankAccountId, logOffset]);

    const {
        data: changeLogs,
        isLoading: isLoadingChangeLogs,
        refetch: refetchChangeLogs,
    } = useGetInterestRateChangeLogs(changeLogQuery);

    const {
        data: liveRecon,
        isFetching: isFetchingRecon,
        refetch: refetchRecon,
    } = useGetInterestRateRecon({}, { enabled: false });

    const {
        data: productLogHistoryRecon,
        isFetching: isFetchingProductLogHistory,
        refetch: refetchProductLogHistory,
    } = useGetInterestRateProductLogHistoryRecon({}, { enabled: false });

    const pendingWindows = schedule?.pendingFutureWindows ?? [];
    const [selectedScheduleKey, setSelectedScheduleKey] = useState<string>('');

    useEffect(() => {
        if (pendingWindows.length === 0) {
            setSelectedScheduleKey('');
            return;
        }
        if (
            !pendingWindows.some((w) => w.scheduleKey === selectedScheduleKey)
        ) {
            setSelectedScheduleKey(pendingWindows[0].scheduleKey);
        }
    }, [pendingWindows, selectedScheduleKey]);

    const selectedPendingWindow =
        pendingWindows.find((w) => w.scheduleKey === selectedScheduleKey) ??
        null;

    const [retryLog, setRetryLog] =
        useState<InterestRateChangeLogListItem | null>(null);
    const [historyMismatchDetail, setHistoryMismatchDetail] =
        useState<ProductLogHistoryMismatch | null>(null);
    const [isMassConfirmOpen, setIsMassConfirmOpen] = useState(false);
    const [isRetryConfirmOpen, setIsRetryConfirmOpen] = useState(false);

    const { mutateAsync: triggerMassTierChange, isPending: isMassPending } =
        useTriggerMassTierChange();
    const { mutateAsync: retryMembershipSync, isPending: isRetryPending } =
        useRetryMembershipInterestRateSync();

    const displayedRecon = liveRecon ?? audit?.lastRecon ?? null;
    const hasOutOfSync =
        (displayedRecon?.outOfSyncCount ?? 0) > 0 ||
        (displayedRecon?.mismatches.length ?? 0) > 0;

    const handleMassTierSubmit = async () => {
        setIsMassConfirmOpen(false);
        if (selectedPendingWindow == null) {
            return;
        }
        const result = await triggerMassTierChange({
            body: {
                tier: selectedPendingWindow.tier,
                effectivePostedDate: selectedPendingWindow.validFrom,
            },
        });
        toast.show(
            `Mass tier change started: ${result.workflowID}`,
            MutationSuccessToastOptions,
        );
        await Promise.all([
            refetchAudit(),
            refetchChangeLogs(),
            refetchSchedule(),
        ]);
    };

    const handleRetrySubmit = async () => {
        setIsRetryConfirmOpen(false);
        if (retryLog == null) {
            return;
        }
        const result = await retryMembershipSync({
            body: { changeLogId: retryLog.id },
        });
        toast.show(
            result.clamped
                ? `Retry started from ${result.validFrom} (clamped from ${result.originalValidFrom})`
                : `Retry started from ${result.validFrom}`,
            MutationSuccessToastOptions,
        );
        setRetryLog(null);
        await Promise.all([refetchAudit(), refetchChangeLogs()]);
    };

    const handleLiveRecon = async () => {
        await refetchRecon();
        await refetchAudit();
    };

    const handleProductLogHistoryRecon = async () => {
        setHistoryMismatchDetail(null);
        await refetchProductLogHistory();
    };

    if (isLoadingSchedule || isLoadingAudit) {
        return <Spinner />;
    }

    return (
        <YStack gap={24} width="100%">
            <YStack gap={8}>
                <H6Emphasized>Rate plans (source of truth)</H6Emphasized>
                <BodySmallRegular>
                    Cutover {schedule?.cutoverDate} · Current bank posted day{' '}
                    {schedule?.currentBankPostedDate}
                </BodySmallRegular>
                <XStack gap={16} flexWrap="wrap">
                    {(['STANDARD', 'PREMIUM'] as const).map((tier) => (
                        <YStack key={tier} gap={8} flex={1} minWidth={280}>
                            <BodyMediumRegular>{tier}</BodyMediumRegular>
                            <DataTable
                                value={schedule?.tiers[tier] ?? []}
                                size="small"
                            >
                                <Column field="validFrom" header="From" />
                                <Column
                                    field="validTo"
                                    header="To"
                                    body={(row) => row.validTo ?? 'open'}
                                />
                                <Column field="rateBps" header="bps" />
                                <Column field="apyDisplay" header="APY" />
                            </DataTable>
                        </YStack>
                    ))}
                </XStack>
            </YStack>

            <YStack gap={8}>
                <XStack justifyContent="space-between" alignItems="center">
                    <H6Emphasized>Open accounts by tier</H6Emphasized>
                    <Button
                        size="small"
                        onPress={() => {
                            void refetchAudit();
                        }}
                    >
                        Refresh
                    </Button>
                </XStack>
                <BodySmallRegular>
                    Active depository:{' '}
                    {audit?.activeDepositoryAccountCount ?? 0} · With
                    membership: {audit?.accountsWithMembershipCount ?? 0} ·
                    Missing product period:{' '}
                    {audit?.accountsMissingProductPeriodCount ?? 0} · Unresolved
                    FAILED logs: {audit?.unresolvedFailedLogCount ?? 0}
                </BodySmallRegular>
                <BodySmallRegular>
                    Accounts = open today by current product-log tier. Pending
                    future change = non-FAILED change log with validFrom after
                    today, or a product-log period that starts after today.
                </BodySmallRegular>
                <DataTable value={audit?.accountsByTier ?? []} size="small">
                    <Column field="tier" header="Tier" />
                    <Column field="accountCount" header="Accounts today" />
                    <Column
                        field="pendingFutureChangeCount"
                        header="Pending future change"
                    />
                    <Column
                        field="expectedApyDisplay"
                        header="Expected APY today"
                        body={(row) => row.expectedApyDisplay ?? '—'}
                    />
                    <Column
                        field="expectedRateBps"
                        header="Expected bps today"
                        body={(row) => row.expectedRateBps ?? '—'}
                    />
                    <Column
                        field="missingInterestProductCount"
                        header="Missing interest product"
                    />
                </DataTable>
            </YStack>

            <YStack
                gap={8}
                padding={16}
                borderRadius={8}
                backgroundColor={hasOutOfSync ? '$red200' : '$gray100'}
            >
                <XStack justifyContent="space-between" alignItems="center">
                    <H6Emphasized>
                        {hasOutOfSync
                            ? 'Out of sync (needs attention)'
                            : 'Synctera recon'}
                    </H6Emphasized>
                    <Button
                        size="small"
                        disabled={isFetchingRecon}
                        onPress={() => {
                            void handleLiveRecon();
                        }}
                    >
                        {isFetchingRecon
                            ? 'Running live recon…'
                            : 'Run live Synctera recon'}
                    </Button>
                </XStack>
                {displayedRecon == null ? (
                    <BodySmallRegular>
                        No recon snapshot yet. Nightly recon will populate this;
                        use live recon to refresh now (Synctera fan-out).
                    </BodySmallRegular>
                ) : (
                    <YStack gap={8}>
                        <BodySmallRegular>
                            Last run {displayedRecon.startedAt} –{' '}
                            {displayedRecon.finishedAt} · Checked{' '}
                            {displayedRecon.checkedCount} · Out of sync{' '}
                            {displayedRecon.outOfSyncCount} · Missing product{' '}
                            {displayedRecon.missingInterestProductCount} ·
                            Failed logs{' '}
                            {displayedRecon.unresolvedFailedLogCount}
                        </BodySmallRegular>
                        {displayedRecon.mismatches.length > 0 ? (
                            <DataTable
                                value={displayedRecon.mismatches}
                                size="small"
                            >
                                <Column
                                    field="bankAccountId"
                                    header="Account"
                                />
                                <Column
                                    field="syncteraAccountId"
                                    header="Synctera account"
                                />
                                <Column
                                    field="membershipId"
                                    header="Membership"
                                />
                                <Column field="tier" header="Tier" />
                                <Column
                                    field="expectedBps"
                                    header="Expected bps today"
                                />
                                <Column
                                    field="actualBps"
                                    header="Actual bps today"
                                    body={(row) => row.actualBps ?? '—'}
                                />
                                <Column
                                    field="futureValidFrom"
                                    header="Future from"
                                    body={(row) => row.futureValidFrom ?? '—'}
                                />
                                <Column
                                    field="futureExpectedBps"
                                    header="Future expected bps"
                                    body={(row) => row.futureExpectedBps ?? '—'}
                                />
                                <Column
                                    field="futureActualBps"
                                    header="Future actual bps"
                                    body={(row) => row.futureActualBps ?? '—'}
                                />
                            </DataTable>
                        ) : (
                            <BodySmallRegular>
                                No mismatches in the last recon sample.
                            </BodySmallRegular>
                        )}
                    </YStack>
                )}
            </YStack>

            <YStack
                gap={8}
                padding={16}
                borderRadius={8}
                backgroundColor={
                    (productLogHistoryRecon?.outOfSyncCount ?? 0) > 0
                        ? '$red200'
                        : '$gray100'
                }
            >
                <XStack justifyContent="space-between" alignItems="center">
                    <H6Emphasized>
                        Product-log history vs last change log
                    </H6Emphasized>
                    <Button
                        size="small"
                        disabled={isFetchingProductLogHistory}
                        onPress={() => {
                            void handleProductLogHistoryRecon();
                        }}
                    >
                        {isFetchingProductLogHistory
                            ? 'Running history check…'
                            : 'Run product-log history check'}
                    </Button>
                </XStack>
                <BodySmallRegular>
                    Ad-hoc only (not nightly). Rebuilds expected Synctera
                    rates[] from every membership product-log period, then
                    compares to rates on the latest SUCCEEDED/SKIPPED change-log
                    row for each account. Manual Synctera edits after that row
                    are not visible here — use live Synctera recon for that.
                </BodySmallRegular>
                {productLogHistoryRecon == null ? (
                    <BodySmallRegular>
                        Not run yet this session.
                    </BodySmallRegular>
                ) : (
                    <YStack gap={8}>
                        <BodySmallRegular>
                            Checked {productLogHistoryRecon.checkedCount} · Out
                            of sync {productLogHistoryRecon.outOfSyncCount} ·
                            Missing product log{' '}
                            {productLogHistoryRecon.missingProductLogCount} ·
                            Missing change log{' '}
                            {productLogHistoryRecon.missingChangeLogCount} · No
                            rates in change log{' '}
                            {
                                productLogHistoryRecon.missingRatesInChangeLogCount
                            }
                        </BodySmallRegular>
                        {productLogHistoryRecon.mismatches.length > 0 ? (
                            <DataTable
                                value={productLogHistoryRecon.mismatches}
                                size="small"
                                rowHover
                                onRowClick={(event: DataTableRowClickEvent) => {
                                    const row =
                                        event.data as ProductLogHistoryMismatch;
                                    setHistoryMismatchDetail(row);
                                }}
                                rowClassName={() =>
                                    'interest-rate-history-mismatch-row'
                                }
                            >
                                <Column
                                    field="bankAccountId"
                                    header="Account"
                                />
                                <Column
                                    field="syncteraAccountId"
                                    header="Synctera account"
                                />
                                <Column
                                    field="membershipId"
                                    header="Membership"
                                />
                                <Column field="reason" header="Reason" />
                                <Column
                                    field="firstDiffValidFrom"
                                    header="First diff from"
                                    body={(row) =>
                                        row.firstDiffValidFrom ?? '—'
                                    }
                                />
                                <Column
                                    field="expectedBpsAtDiff"
                                    header="Expected bps"
                                    body={(row) => row.expectedBpsAtDiff ?? '—'}
                                />
                                <Column
                                    field="actualBpsAtDiff"
                                    header="Actual bps"
                                    body={(row) => row.actualBpsAtDiff ?? '—'}
                                />
                                <Column
                                    field="changeLogId"
                                    header="Change log"
                                    body={(row) => row.changeLogId ?? '—'}
                                />
                            </DataTable>
                        ) : (
                            <BodySmallRegular>
                                No mismatches in the sample.
                            </BodySmallRegular>
                        )}
                        {historyMismatchDetail != null ? (
                            <YStack gap={8}>
                                <XStack
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <BodyMediumRegular>
                                        Rates JSON ·{' '}
                                        {historyMismatchDetail.bankAccountId} ·{' '}
                                        {historyMismatchDetail.reason}
                                    </BodyMediumRegular>
                                    <Button
                                        size="small"
                                        onPress={() => {
                                            setHistoryMismatchDetail(null);
                                        }}
                                    >
                                        Close
                                    </Button>
                                </XStack>
                                <YStack gap={4}>
                                    <BodySmallRegular>
                                        Membership product log (
                                        {historyMismatchDetail.membershipId})
                                    </BodySmallRegular>
                                    {(historyMismatchDetail.productLogPeriods
                                        ?.length ?? 0) === 0 ? (
                                        <BodySmallRegular>
                                            No product-log periods on this
                                            membership.
                                        </BodySmallRegular>
                                    ) : (
                                        <DataTable
                                            value={
                                                historyMismatchDetail.productLogPeriods
                                            }
                                            size="small"
                                        >
                                            <Column
                                                field="postedDateFrom"
                                                header="From"
                                            />
                                            <Column
                                                field="postedDateTo"
                                                header="To"
                                                body={(row) =>
                                                    row.postedDateTo ?? 'open'
                                                }
                                            />
                                            <Column
                                                field="productId"
                                                header="Product"
                                            />
                                            <Column
                                                field="tier"
                                                header="Tier"
                                                body={(row) => row.tier ?? '—'}
                                            />
                                        </DataTable>
                                    )}
                                </YStack>
                                {historyMismatchDetail.reason ===
                                    'ARRAY_MISMATCH' &&
                                (historyMismatchDetail.expectedRates != null ||
                                    historyMismatchDetail.actualRates !=
                                        null) ? (
                                    <RatesJsonSideBySideDiff
                                        expectedRates={
                                            historyMismatchDetail.expectedRates
                                        }
                                        actualRates={
                                            historyMismatchDetail.actualRates
                                        }
                                    />
                                ) : historyMismatchDetail.expectedRates ==
                                      null &&
                                  historyMismatchDetail.actualRates == null ? (
                                    <BodySmallRegular>
                                        No rates arrays on this row (missing
                                        product log / change log). Re-run after
                                        restarting the backend if you expected
                                        ARRAY_MISMATCH JSON.
                                    </BodySmallRegular>
                                ) : (
                                    <pre
                                        style={{
                                            margin: 0,
                                            padding: 8,
                                            overflow: 'auto',
                                            maxHeight: 360,
                                            fontFamily: 'monospace',
                                            fontSize: 12,
                                            backgroundColor: '#fff',
                                            borderRadius: 4,
                                        }}
                                    >
                                        {JSON.stringify(
                                            {
                                                expectedRates:
                                                    historyMismatchDetail.expectedRates,
                                                actualRates:
                                                    historyMismatchDetail.actualRates,
                                            },
                                            null,
                                            2,
                                        )}
                                    </pre>
                                )}
                            </YStack>
                        ) : productLogHistoryRecon.mismatches.length > 0 ? (
                            <BodySmallRegular>
                                Click a mismatch row to inspect expected vs
                                actual rates JSON.
                            </BodySmallRegular>
                        ) : null}
                    </YStack>
                )}
            </YStack>

            <YStack gap={8}>
                <H6Emphasized>Change logs</H6Emphasized>
                <XStack gap={8} flexWrap="wrap" alignItems="center">
                    <Dropdown
                        value={logStatus}
                        options={STATUS_OPTIONS}
                        optionLabel="label"
                        optionValue="value"
                        onChange={(e) => {
                            setLogOffset(0);
                            setLogStatus(
                                typeof e.value === 'string' ? e.value : '',
                            );
                        }}
                        placeholder="Status"
                        style={{ minWidth: 160 }}
                    />
                    <InputText
                        value={logMembershipId}
                        onChange={(e) => {
                            setLogOffset(0);
                            setLogMembershipId(e.target.value);
                        }}
                        placeholder="membershipId"
                    />
                    <InputText
                        value={logBankAccountId}
                        onChange={(e) => {
                            setLogOffset(0);
                            setLogBankAccountId(e.target.value);
                        }}
                        placeholder="bankAccountId"
                    />
                </XStack>
                {isLoadingChangeLogs ? (
                    <Spinner />
                ) : (
                    <>
                        <DataTable
                            value={changeLogs?.items ?? []}
                            size="small"
                            paginator={false}
                        >
                            <Column field="createdAt" header="Created" />
                            <Column field="status" header="Status" />
                            <Column field="reason" header="Reason" />
                            <Column field="tier" header="Tier" />
                            <Column field="rateBps" header="bps" />
                            <Column field="validFrom" header="validFrom" />
                            <Column field="membershipId" header="Membership" />
                            <Column field="bankAccountId" header="Account" />
                            <Column field="error" header="Error" />
                            <Column field="workflowId" header="Workflow" />
                            <Column
                                header=""
                                body={(row: InterestRateChangeLogListItem) =>
                                    row.status === 'FAILED' &&
                                    row.reason ===
                                        'MEMBERSHIP_PRODUCT_CHANGE' ? (
                                        <Button
                                            size="small"
                                            disabled={isRetryPending}
                                            onPress={() => {
                                                setRetryLog(row);
                                                setIsRetryConfirmOpen(true);
                                            }}
                                        >
                                            Retry
                                        </Button>
                                    ) : null
                                }
                            />
                        </DataTable>
                        <XStack gap={8} alignItems="center">
                            <BodySmallRegular>
                                {changeLogs?.total ?? 0} total · offset{' '}
                                {logOffset}
                            </BodySmallRegular>
                            <Button
                                size="small"
                                disabled={logOffset <= 0}
                                onPress={() => {
                                    setLogOffset(
                                        Math.max(0, logOffset - logLimit),
                                    );
                                }}
                            >
                                Prev
                            </Button>
                            <Button
                                size="small"
                                disabled={
                                    logOffset + logLimit >=
                                    (changeLogs?.total ?? 0)
                                }
                                onPress={() => {
                                    setLogOffset(logOffset + logLimit);
                                }}
                            >
                                Next
                            </Button>
                        </XStack>
                    </>
                )}
            </YStack>

            <YStack gap={8}>
                <H6Emphasized>Actions</H6Emphasized>
                <YStack
                    gap={12}
                    padding={16}
                    borderRadius={8}
                    backgroundColor="$gray100"
                >
                    <BodyMediumRegular>
                        Path 2: mass tier rate change
                    </BodyMediumRegular>
                    <BodySmallRegular>
                        Only pending future schedule windows (validFrom after
                        current bank day, not yet mass-applied) can be started.
                        Add the window in code first, then apply it here.
                    </BodySmallRegular>
                    {pendingWindows.length === 0 ? (
                        <BodySmallRegular>
                            No pending future rate windows right now.
                        </BodySmallRegular>
                    ) : (
                        <XStack gap={8} flexWrap="wrap" alignItems="center">
                            <Dropdown
                                value={selectedScheduleKey}
                                options={pendingWindows.map((w) => ({
                                    label: pendingWindowLabel(w),
                                    value: w.scheduleKey,
                                }))}
                                optionLabel="label"
                                optionValue="value"
                                onChange={(e) => {
                                    setSelectedScheduleKey(
                                        typeof e.value === 'string'
                                            ? e.value
                                            : '',
                                    );
                                }}
                                style={{ minWidth: 360 }}
                            />
                            <Button
                                size="small"
                                disabled={
                                    isMassPending ||
                                    selectedPendingWindow == null
                                }
                                onPress={() => {
                                    setIsMassConfirmOpen(true);
                                }}
                            >
                                Start mass change
                            </Button>
                        </XStack>
                    )}
                </YStack>
            </YStack>

            <ConfirmModal
                isOpen={isMassConfirmOpen}
                onClose={() => {
                    setIsMassConfirmOpen(false);
                }}
                description={`Start Path 2 for ${selectedPendingWindow?.tier ?? '?'} effective ${selectedPendingWindow?.validFrom ?? '?'} (${selectedPendingWindow?.apyDisplay ?? 'unknown'} / ${selectedPendingWindow?.rateBps ?? '?'} bps)?`}
                yesText="Start workflow"
                onConfirm={() => {
                    void handleMassTierSubmit();
                }}
            />
            <ConfirmModal
                isOpen={isRetryConfirmOpen}
                onClose={() => {
                    setIsRetryConfirmOpen(false);
                }}
                description={`Retry the failed membership product change for ${retryLog?.membershipId ?? '?'} (original validFrom ${retryLog?.validFrom ?? '?'})? If that date is no longer legal, the closest future bank day is used and banking ops is warned.`}
                yesText="Start workflow"
                onConfirm={() => {
                    void handleRetrySubmit();
                }}
            />
            <style>{`
                .p-datatable .interest-rate-history-mismatch-row {
                    cursor: pointer;
                }
            `}</style>
        </YStack>
    );
};
